import React from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FAQ {
  question: string;
  answer: string;
}

interface SeoFaqSectionProps {
  faqs?: FAQ[];
}

// Utility function to parse HTML and convert anchor tags to Link components
const parseHtmlWithLinks = (htmlString: string): React.ReactNode => {
  // Use regex to find and replace anchor tags
  const linkRegex = /<a\s+([^>]*?)>(.*?)<\/a>/gi;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let linkIndex = 0;

  while ((match = linkRegex.exec(htmlString)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      const beforeText = htmlString.substring(lastIndex, match.index);
      if (beforeText.trim()) {
        parts.push(beforeText);
      }
    }

    // Parse the link attributes
    const attributes = match[1];
    const linkText = match[2];
    
    // Extract href and class attributes
    const hrefMatch = attributes.match(/href=["']([^"']*)["']/i);
    const classMatch = attributes.match(/class=["']([^"']*)["']/i);
    
    const href = hrefMatch ? hrefMatch[1] : '';
    const className = classMatch ? classMatch[1] : '';

    // Add the Link component
    if (href) {
      parts.push(
        <Link key={`link-${linkIndex++}`} to={href} className={className}>
          {linkText}
        </Link>
      );
    } else {
      // If no href, just add the text
      parts.push(linkText);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last link
  if (lastIndex < htmlString.length) {
    const remainingText = htmlString.substring(lastIndex);
    if (remainingText.trim()) {
      parts.push(remainingText);
    }
  }

  // If no links were found, return the original string
  if (parts.length === 0) {
    return htmlString;
  }

  return parts;
};

const SeoFaqSection = ({ faqs: customFaqs }: SeoFaqSectionProps) => {
  const defaultFaqs = [
    {
      question: "Can I create full blogs with gimmefy.ai?",
      answer: "Yes. Studio generates structured drafts with intros, subheads, and CTAs—ready for refinement and publishing."
    },
    {
      question: "Will the blogs sound like my brand?",
      answer: "Always. Brand Vault ensures every draft reflects your voice, style, and guidelines."
    },
    {
      question: "Can gimmefy help plan an ongoing blog calendar?",
      answer: "Yes. The Blog Calendar Agent suggests seasonal topics and campaign-aligned hooks so your blog pipeline stays full."
    },
    {
      question: "Can I publish blogs directly from gimmefy?",
      answer: "You can export content to your CMS or publishing tools. Native publishing integrations are available for common platforms."
    },
    {
      question: "Does gimmefy support SEO-friendly blogs?",
      answer: "Yes. Studio generates keyword-rich outlines, headers, and meta descriptions to boost search visibility."
    },
    {
      question: "Is multilingual blogging supported?",
      answer: "Yes. gimmefy supports multiple languages, letting you localise content at scale."
    },
    {
      question: "Can I repurpose blogs into other formats?",
      answer: "Yes. Turn blog drafts into LinkedIn posts, carousels, or email newsletters in a few clicks."
    },
    {
      question: "Is my data secure?",
      answer: "Yes. Enterprise-grade security and Brand Vault privacy keep your drafts, data, and brand assets protected. For more information, contact us at <a href='/contact' class='text-primary hover:underline'>support@gimmefy.ai</a>."
    }
  ];

  const faqs = customFaqs || defaultFaqs;

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
              Frequently asked questions
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="w-full space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-border rounded-lg px-4 sm:px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary text-sm sm:text-base py-4 sm:py-6 min-h-[48px] touch-manipulation">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base pb-4 sm:pb-6">
                  <div>{parseHtmlWithLinks(faq.answer)}</div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default SeoFaqSection;